import type {FetchWrapper} from '@/ts/api/fetch-wrapper';
import type {
  GetInterventionResponse,
  GetInterventionsResponse,
  GetQuestionnaireCustomAnswersResponse,
  GetBlockContentResponse,
  Homework,
  Questionnaire,
  QuestionnaireReview,
  Session,
  StayOfferResponse,
} from '@/ts/types/dto/interventions-dto';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import type {
  PutSessionRequest,
  QuestionnaireAnswers,
} from '@/ts/types/dto/session-request-dto';
import type {ContentHomeResponse} from '@/ts/types/dto/home-dto';
import type {
  UserResponse,
  ConsentApprovalRequest,
  PurchaseLicense,
  Contract,
  SubscriptionResponse,
  PaymentMethodInfo,
  SetupIntentResponse,
} from '@/ts/types/dto/user-dto';
import type {ChangeCurrentUserPasswordRequest} from '@/ts/types/dto/change-password-dto';
import type {
  InsuranceAcceptRequest,
  InsuranceAcceptResponse,
  InsuranceVerificationRequest,
  InsuranceVerificationResponse,
  PayerDto,
} from '@/ts/types/dto/insurance-dto';
import type {
  GetJournalsResponse,
  JournalTemplateDTO,
  PostJournalRequest,
  PostJournalResponse,
  GetJournalsParams,
  DayMoodsResponse,
  GetDayMoodsParams,
} from '@/ts/types/dto/journals-dto';
import type {GetTodayResponse} from '@/ts/types/dto/today-dto';

/**
 * {@link https://api-dev.ip.significo.dev/swagger-ui/index.html?urls.primaryName=me API
 */
export class MeApi {
  public constructor(private readonly fetch: FetchWrapper) {
  }

  public async getInterventions(): Promise<GetInterventionsResponse> {
    return this.fetch.get({
      url: '/interventions',
    });
  }

  public async getIntervention(id: UuidDTO): Promise<GetInterventionResponse> {
    return this.fetch.get({
      url: `/interventions/${id}`,
    });
  }

  public async postQuestionnaire(body: QuestionnaireAnswers[]): Promise<void> {
    return this.fetch.post({
      url: '/questionnaire',
      body,
      emptyResponse: true,
    });
  }

  public async getQuestionnaire(questionnaireId: UuidDTO): Promise<Questionnaire> {
    return this.fetch.get({
      url: `/questionnaire/${questionnaireId}`,
    });
  }

  public async changePassword(body: ChangeCurrentUserPasswordRequest): Promise<void> {
    return this.fetch.put({
      url: '/change-password',
      body,
      emptyResponse: true,
    });
  }

  public async getQuestionnaireReview(
    interventionId: UuidDTO,
    sessionId: UuidDTO,
    questionnaireReviewId: UuidDTO,
  ): Promise<QuestionnaireReview> {
    return this.fetch.get({
      url: `/interventions/${interventionId}/sessions/${sessionId}/questionnaire-review/${questionnaireReviewId}`,
    });
  }


  public async unlockSession(
    interventionId: UuidDTO,
  ): Promise<QuestionnaireReview> {
    return this.fetch.put({
      emptyResponse: true,
      url: `/qa/interventions/${interventionId}/unlock-current-session`,
    });
  }

  public async getMe(): Promise<UserResponse> {
    return this.fetch.get({
      url: '',
    });
  }

  public async updateMe(body: {
    name?: string;
    gender?: string;
    birthYear?: number;
    educationLevel?: string;
    colorMode?: string;
    highContrast?: boolean;
    minFontSize?: number;
    languageCode?: string;
    onboardingTourVersion?: number | null;
    legalDocsHintShown?: boolean | null;
  }): Promise<void> {
    return this.fetch.put({
      url: '',
      body,
      emptyResponse: true,
    });
  }

  public async getHomeContent(): Promise<ContentHomeResponse> {
    return this.fetch.get({
      url: '/home-content',
    });
  }

  public async putSession(
    interventionId: UuidDTO,
    sessionId: UuidDTO,
    pageId: UuidDTO,
    body: PutSessionRequest,
  ): Promise<GetInterventionResponse> {
    return this.fetch.put({
      url: `/interventions/${interventionId}/sessions/${sessionId}/pages/${pageId}`,
      body,
    });
  }

  public async putHomework(
    interventionId: UuidDTO,
    sessionId: UuidDTO,
    userHomeworkId: UuidDTO,
    body: PutSessionRequest,
  ): Promise<GetInterventionResponse> {
    return this.fetch.put({
      url: `/interventions/${interventionId}/sessions/${sessionId}/homework/${userHomeworkId}`,
      body,
    });
  }

  public async postHomeWork(
    interventionId: UuidDTO,
    sessionId: UuidDTO,
  ): Promise<Homework> {
    return this.fetch.post({
      url: `/interventions/${interventionId}/sessions/${sessionId}/homework`,
    });
  }

  public async enrollIntervention(id: UuidDTO): Promise<GetInterventionResponse> {
    return this.fetch.post({
      url: `/interventions/${id}/enroll`,
    });
  }

  public async withdrawIntervention(id: UuidDTO): Promise<void> {
    return this.fetch.delete({
      url: `/interventions/${id}/enroll`,
      emptyResponse: true,
    });
  }

  public async getSession(interventionId: UuidDTO, sessionId: UuidDTO): Promise<Session> {
    return this.fetch.get({
      url: `/interventions/${interventionId}/sessions/${sessionId}`,
    });
  }

  public async getPurchasableLicenses(): Promise<PurchaseLicense[]> {
    return this.fetch.get({
      url: '/licenses/purchasable',
    });
  }

  public async getContractsHistory(): Promise<Contract[]> {
    return this.fetch.get({
      url: '/licenses/contracts',
    });
  }

  public async createSubscriptionForLicense(licenseId: string): Promise<SubscriptionResponse> {
    return this.fetch.post({
      url: `/licenses/${licenseId}/subscribe`,
    });
  }

  public async cancelSubscription(): Promise<void> {
    return this.fetch.delete({
      url: '/licenses/subscription',
      emptyResponse: true,
    });
  }

  public async reactivateSubscription(): Promise<void> {
    return this.fetch.put({
      url: '/licenses/subscription',
      emptyResponse: true,
    });
  }

  public async getQuestionnaireCustomAnswers(): Promise<GetQuestionnaireCustomAnswersResponse> {
    return this.fetch.get({
      url: '/interventions/questionnaire-custom-answers',
    });
  }

  /**
   * Records that the user has been shown the published Instructions For Use. Sends no body: the
   * backend reads the published version itself, so the client cannot record one that never existed.
   */
  public async acknowledgeIfu(): Promise<UserResponse> {
    return this.fetch.post({
      url: '/ifu-acknowledgement',
    });
  }

  public async putConsentApproval(body: ConsentApprovalRequest): Promise<void> {
    return this.fetch.put({
      url: '/consent-approval',
      body,
      emptyResponse: true,
    });
  }

  public async getBlockContent(contentId: UuidDTO): Promise<GetBlockContentResponse> {
    return this.fetch.get({
      url: `/block-content/${contentId}`,
    });
  }

  public async postBlockContentRating(
    contentId: UuidDTO,
    body: { rating: 'like' | 'dislike' | 'unrated' },
  ): Promise<void> {
    return this.fetch.post({
      url: `/block-content/${contentId}/rating?rating=${body.rating}`,
      emptyResponse: true,
    });
  }

  /**
   * Marks the piece as watched. The backend stamps the moment, which is what closes the intro
   * video's window: from then on the video belongs in the FAQ rather than on the Today page.
   */
  public async postBlockContentWatched(contentId: UuidDTO): Promise<void> {
    return this.fetch.post({
      url: `/block-content/${contentId}/watched`,
      emptyResponse: true,
      // Nobody asked for this call, so an expired session must not end someone's viewing over it.
      keepSessionOn401: true,
    });
  }

  /**
   * Records that the piece was actually put in front of the reader, which is a different claim
   * from having watched it: the welcome video plays itself, and a video that played says nothing
   * about whether anyone was watching (IPD-3114). The stamp only stops the next visit starting it
   * again — the tile keeps its place until it is watched or its window closes.
   */
  public async postBlockContentPresented(contentId: UuidDTO): Promise<void> {
    return this.fetch.post({
      url: `/block-content/${contentId}/presented`,
      emptyResponse: true,
      // Same reason as the watch above: nobody asked for it.
      keepSessionOn401: true,
    });
  }

  public async postBlockContentBookmark(
    contentId: UuidDTO,
    body: { isBookmarked: boolean },
  ): Promise<void> {
    return this.fetch.post({
      url: `/block-content/${contentId}/bookmark?isBookmarked=${body.isBookmarked}`,
      emptyResponse: true,
    });
  }

  public async getPaymentMethod(): Promise<PaymentMethodInfo | null> {
    return this.fetch.get({
      url: '/licenses/payment-method',
    });
  }

  public async createPaymentMethodSetupIntent(): Promise<SetupIntentResponse> {
    return this.fetch.post({
      url: '/licenses/payment-method/setup-intent',
    });
  }

  public async updatePaymentMethod(paymentMethodId: string): Promise<void> {
    return this.fetch.put({
      url: '/licenses/payment-method',
      body: {paymentMethodId},
      emptyResponse: true,
    });
  }

  public async renewLicense(voucherCode: string): Promise<Contract> {
    return this.fetch.post({
      url: '/licenses/renew',
      body: {voucherCode},
    });
  }

  /**
   * The whole Today page in one call.
   *
   * Always pass the user's IANA timezone: without it the backend falls back to the server's own
   * zone, and every calendar-day rule of the page — the greeting, the all-caught-up state that
   * lasts until the end of the day, the promo of the day, the three-day content window — would be
   * resolved against the wrong day.
   *
   * The call is not free of side effects, which is why it takes a request of its own rather than
   * riding along with the shell's `me` fetch: reading it fixes the content pair for the window and
   * can mark an expired challenge as failed. So it is made when the page is actually shown, and
   * never prefetched or called speculatively.
   */
  public async getToday(timezone: string): Promise<GetTodayResponse> {
    return this.fetch.get({
      url: '/today',
      queryParams: {timezone},
    });
  }

  public async getJournals(
    {page = 0, size = 10, startDate, endDate, timezone, interventionId, moods}: GetJournalsParams = {},
  ): Promise<GetJournalsResponse> {
    return this.fetch.get({
      url: '/journals',
      queryParams: {
        page,
        size,
        ...(startDate && {startDate}),
        ...(endDate && {endDate}),
        ...(timezone && {timezone}),
        ...(interventionId && {interventionId}),
        // Comma-separated, which is how Spring binds a repeated param to a List. An empty
        // selection is left off entirely: the backend reads "no moods" as "every mood", and
        // sending an empty value would ask it to match nothing.
        ...(moods && moods.length > 0 && {moods: moods.join(',')}),
      },
    });
  }

  public async getJournalTemplate(): Promise<JournalTemplateDTO> {
    return this.fetch.get({
      url: '/journals/template',
    });
  }

  public async postJournal(body: PostJournalRequest): Promise<PostJournalResponse> {
    return this.fetch.post({
      url: '/journals',
      body,
    });
  }

  public async getDayMoods({startDate, endDate, timezone}: GetDayMoodsParams): Promise<DayMoodsResponse> {
    return this.fetch.get({
      url: '/journals/moods',
      queryParams: {
        ...(startDate && {startDate}),
        ...(endDate && {endDate}),
        ...(timezone && {timezone}),
      },
    });
  }

  public async logout(): Promise<void> {
    return this.fetch.post({
      url: '/logout',
      emptyResponse: true,
    });
  }

  public async deleteAccount(): Promise<void> {
    return this.fetch.delete({
      url: '',
      emptyResponse: true,
    });
  }

  public async verifyInsurance(body: InsuranceVerificationRequest): Promise<InsuranceVerificationResponse> {
    return this.fetch.post({
      url: '/insurance/verify',
      body,
    });
  }

  public async getInsurancePayers(): Promise<PayerDto[]> {
    return this.fetch.get({
      url: '/insurance/payers',
    });
  }

  public async acceptInsurance(body: InsuranceAcceptRequest): Promise<InsuranceAcceptResponse> {
    return this.fetch.post({
      url: '/insurance/accept',
      body,
    });
  }

  /** Delete a stored verification — used when the user declines to keep their insurance data. */
  public async deleteInsuranceVerification(verificationId: string): Promise<void> {
    return this.fetch.delete({
      url: `/insurance/verifications/${verificationId}`,
      emptyResponse: true,
    });
  }

  /**
   * Accept the stay offer for an intervention — the fallback grant offered so an eligible enrollee
   * can complete the programme when insurance didn't fund their access. Server-gated (requires an
   * in-progress, eligible enrollment). Idempotent; cancels a paid subscription if present.
   */
  public async acceptStayOffer(interventionId: string): Promise<StayOfferResponse> {
    return this.fetch.post({
      url: `/interventions/${interventionId}/stay-offer`,
    });
  }

  /**
   * Record that the user resolved how to continue after seeing their CDC DPP eligibility outcome
   * (terminal choice in the post-questionnaire dialog, both eligible and not-eligible). Empty body;
   * the server stamps the instant. Idempotent. Read back as `cdcDpp.eligibilityAck`, which gates
   * the dialog consistently across tabs and devices.
   */
  public async acknowledgeCdcDppEligibility(interventionId: string): Promise<void> {
    return this.fetch.post({
      url: `/interventions/${interventionId}/cdc-dpp/acknowledge`,
      emptyResponse: true,
    });
  }
}
